/**
 * Threshold selection — golden cases 6, 7 and 11. Stage-aware by
 * construction (ADR-0003, register §4):
 *
 * - EC always selects the standard threshold. There is no entry-clearance
 *   transitional provision (E-ECP.3.1 has no £18,600 limb).
 * - At LTR/ILR the transitional threshold applies only when ALL FOUR
 *   conditions hold: first applied before the cutoff · granted on that
 *   application · still holds five-year-route partner permission · applying
 *   to stay with the same partner.
 * - Unknown transitional facts fail closed to PENDING_EVIDENCE — assuming
 *   either threshold can misstate the requirement in one direction or the
 *   other, and the £27,900 gap is exactly the error class the golden corpus
 *   exists to prevent.
 *
 * MET here means "the applicable threshold is determined", not that any
 * income requirement is satisfied — downstream rules consume the figure.
 */
import { compareISODate } from "../dates.js";
import type { EvaluatePredicate, PredicateOutcome } from "./predicate.js";

export const select_mir_threshold: EvaluatePredicate = (facts, rule, ctx) => {
  const standard = asNumber(ctx.paramFigure(rule, "standard_figure"));
  const transitional = asNumber(ctx.paramFigure(rule, "transitional_figure"));
  const cutoff = asDateString(ctx.paramFigure(rule, "cutoff_figure"));

  const standardOutcome = (note: string): PredicateOutcome => ({
    status: "MET",
    value: "standard",
    figures: { threshold: standard },
    notes: [note],
  });

  if (facts.stage === "EC") {
    return standardOutcome(
      "The standard threshold applies: the lower transitional figure is never available at entry clearance.",
    );
  }

  if (facts.first_application_date === null) {
    return {
      status: "PENDING_EVIDENCE",
      notes: [
        "The date of the first application as a partner or fiancé(e) is needed to determine which threshold applies.",
      ],
    };
  }

  if (compareISODate(facts.first_application_date, cutoff) >= 0) {
    return standardOutcome(
      "The standard threshold applies: the first application was made on or after 11 April 2024.",
    );
  }

  const conditions = [
    facts.transitional.granted_on_first_application,
    facts.transitional.holds_five_year_route_partner_permission,
    facts.transitional.applying_with_same_partner,
  ];

  if (conditions.some((c) => c === null)) {
    return {
      status: "PENDING_EVIDENCE",
      notes: [
        "Whether the lower transitional threshold applies depends on three further facts: whether the first application led to a grant, whether five-year-route partner permission is currently held, and whether this application is to stay with the same partner.",
      ],
    };
  }

  if (conditions.every((c) => c === true)) {
    return {
      status: "MET",
      value: "transitional",
      figures: { threshold: transitional },
      notes: ["All four transitional conditions are met, so the lower threshold applies."],
    };
  }

  return standardOutcome(
    "The standard threshold applies: not all of the transitional conditions are met.",
  );
};

function asNumber(value: number | string): number {
  if (typeof value !== "number") throw new Error(`Expected a numeric figure, got: ${value}`);
  return value;
}

function asDateString(value: number | string): string {
  if (typeof value !== "string") throw new Error(`Expected a date figure, got: ${value}`);
  return value;
}
